import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {GrafanaEmbedComponent} from '../../grafana/grafana-embed';
import {CommonModule, DatePipe} from '@angular/common';
import {VigimareAlertService} from '../../services/vigimare-alert.service';
import {AlertAuditEntry, VigimareAlert} from '../../models/vigimare-alert.model';
import {VigimareVesselResponse} from '../../models/vigimare-vessel.model';
import {VigimareObjectResponse} from '../../models/vigimare-object.model';
import {forkJoin, Subscription} from 'rxjs';
import {AlertAssignment} from '../../models/vigimare-alert.model';
import {KeycloakUser} from '../../models/keycloak-user.model';
import {Organization} from '../../models/organization.model';
import {KeycloakUserService} from '../../services/keycloak-user.service';
import {OrganizationService} from '../../services/organization.service';
import {KeycloakService} from '../../security/keycloak.service';
import {APP_ROLES} from '../../security/roles';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-incidents',
  imports: [GrafanaEmbedComponent, CommonModule, DatePipe],
  templateUrl: './incidents.html',
  styleUrl: './incidents.scss',
})
export class IncidentsComponent implements OnInit {

  private alertService = inject(VigimareAlertService);
  private keycloakUserService = inject(KeycloakUserService);
  private organizationService = inject(OrganizationService);
  private keycloakService = inject(KeycloakService);
  private route = inject(ActivatedRoute);

  private sseSubscription?: Subscription;
  private queryParamsSub?: Subscription;

  highlightedAlertId = signal<number | null>(null);
  alerts = signal<VigimareAlert[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  currentPage = signal(1);
  pageSize = signal(5);

  globalFilter = signal('');
  sortField = signal<'generatedIn' | 'id'>('id');
  sortDirection = signal<'asc' | 'desc'>('desc');

  deleteModalOpen = signal(false);
  alertIdToDelete = signal<number | null>(null);

  detailsModalOpen = signal(false);
  selectedAlert = signal<VigimareAlert | null>(null);

  involvedObjectModalOpen = signal(false);
  involvedObjectLoading = signal(false);
  involvedObjectError = signal<string | null>(null);

  selectedInvolvedObjectType = signal<'vessel' | 'object' | null>(null);
  selectedVessel = signal<VigimareVesselResponse | null>(null);
  selectedObject = signal<VigimareObjectResponse | null>(null);

  assignmentModalOpen = signal(false);
  assignmentLoading = signal(false);
  assignmentSaving = signal(false);
  assignmentError = signal<string | null>(null);
  selectedAssignmentAlert = signal<VigimareAlert | null>(null);
  currentAssignments = signal<AlertAssignment[]>([]);
  availableUsers = signal<KeycloakUser[]>([]);
  availableOrganizations = signal<Organization[]>([]);
  selectedUserIds = signal<string[]>([]);
  selectedOrganizationIds = signal<number[]>([]);
  auditModalOpen = signal(false);
  auditLoading = signal(false);
  auditError = signal<string | null>(null);
  selectedAuditAlert = signal<VigimareAlert | null>(null);
  auditEntries = signal<AlertAuditEntry[]>([]);
  commentModalOpen = signal(false);
  commentSaving = signal(false);
  commentError = signal<string | null>(null);
  selectedCommentAlert = signal<VigimareAlert | null>(null);
  commentText = signal('');

  hasInvolvedObject(alert: VigimareAlert): boolean {
    return !!alert.involvedVesselIds?.length || !!alert.involvedObjectIds?.length;
  }

  getInvolvedObjectType(alert: VigimareAlert): 'vessel' | 'object' | null {
    if (alert.involvedVesselIds?.length) {
      return 'vessel';
    }

    if (alert.involvedObjectIds?.length) {
      return 'object';
    }

    return null;
  }

  getInvolvedObjectButtonLabel(alert: VigimareAlert): string {
    const type = this.getInvolvedObjectType(alert);

    if (type === 'vessel') {
      return 'Vessel';
    }

    if (type === 'object') {
      return 'Object';
    }

    return '-';
  }

  openInvolvedObjectModal(alert: VigimareAlert): void {
    this.involvedObjectModalOpen.set(true);
    this.involvedObjectLoading.set(true);
    this.involvedObjectError.set(null);
    this.selectedVessel.set(null);
    this.selectedObject.set(null);
    this.selectedInvolvedObjectType.set(null);

    if (alert.involvedVesselIds?.length) {
      const vesselId = alert.involvedVesselIds[0];
      this.selectedInvolvedObjectType.set('vessel');

      this.alertService.getVesselById(vesselId).subscribe({
        next: (vessel) => {
          this.selectedVessel.set(vessel);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load vessel details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    if (alert.involvedObjectIds?.length) {
      const objectId = alert.involvedObjectIds[0];
      this.selectedInvolvedObjectType.set('object');

      this.alertService.getObjectById(objectId).subscribe({
        next: (objectDetails) => {
          this.selectedObject.set(objectDetails);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load object details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    this.involvedObjectError.set('No involved object found.');
    this.involvedObjectLoading.set(false);
  }

  closeInvolvedObjectModal(): void {
    this.involvedObjectModalOpen.set(false);
    this.involvedObjectLoading.set(false);
    this.involvedObjectError.set(null);
    this.selectedInvolvedObjectType.set(null);
    this.selectedVessel.set(null);
    this.selectedObject.set(null);
  }

  openDetailsModal(alert: VigimareAlert): void {
    this.selectedAlert.set(alert);
    this.detailsModalOpen.set(true);
  }

  closeDetailsModal(): void {
    this.detailsModalOpen.set(false);
    this.selectedAlert.set(null);
  }

  getAlertTypeLabel(alert: VigimareAlert): string {
    if (alert.anomaly) {
      return 'Anomaly';
    }

    if (alert.intentions?.length) {
      return 'Intention';
    }

    return 'Alert';
  }

  hasDetails(alert: VigimareAlert): boolean {
    return !!alert.anomaly || !!alert.intentions?.length;
  }

  openDeleteModal(id: number): void {
    this.alertIdToDelete.set(id);
    this.deleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.deleteModalOpen.set(false);
    this.alertIdToDelete.set(null);
  }

  confirmDelete(): void {
    const id = this.alertIdToDelete();

    if (id === null) {
      return;
    }

    this.deleteAlert(id);
    this.closeDeleteModal();
  }

  canAssignAlerts(): boolean {
    return this.keycloakService.hasAnyRole([APP_ROLES.ADMIN, APP_ROLES.OPERATOR]);
  }

  canDeleteAlert(): boolean {
    return this.keycloakService.canDeleteAlert();
  }

  canCommentOnIncident(): boolean {
    return this.keycloakService.canCommentOnIncident();
  }

  canViewIncidentAudit(): boolean {
    return this.keycloakService.canViewIncidentAudit();
  }

  hasAnyIncidentAction(): boolean {
    return this.canViewIncidentAudit() || this.canCommentOnIncident() || this.canAssignAlerts() || this.canDeleteAlert();
  }

  openAssignmentModal(alert: VigimareAlert): void {
    if (!this.canAssignAlerts()) {
      return;
    }

    this.selectedAssignmentAlert.set(alert);
    this.assignmentModalOpen.set(true);
    this.assignmentLoading.set(true);
    this.assignmentSaving.set(false);
    this.assignmentError.set(null);
    this.currentAssignments.set([]);
    this.availableUsers.set([]);
    this.availableOrganizations.set([]);
    this.selectedUserIds.set([]);
    this.selectedOrganizationIds.set([]);

    forkJoin({
      users: this.keycloakUserService.getAll(),
      organizations: this.organizationService.getAll(),
      assignments: this.alertService.getAssignments(alert.id)
    }).subscribe({
      next: ({ users, organizations, assignments }) => {
        this.currentAssignments.set(assignments);
        this.availableUsers.set(users);
        this.availableOrganizations.set(organizations);
        this.assignmentLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load assignment data', err);
        this.assignmentError.set('Failed to load assignment data.');
        this.assignmentLoading.set(false);
      }
    });
  }

  closeAssignmentModal(): void {
    this.assignmentModalOpen.set(false);
    this.assignmentLoading.set(false);
    this.assignmentSaving.set(false);
    this.assignmentError.set(null);
    this.selectedAssignmentAlert.set(null);
    this.currentAssignments.set([]);
    this.availableUsers.set([]);
    this.availableOrganizations.set([]);
    this.selectedUserIds.set([]);
    this.selectedOrganizationIds.set([]);
  }

  assignedUsers = computed(() =>
    this.currentAssignments().filter(assignment => assignment.assigneeType === 'USER')
  );

  assignedOrganizations = computed(() =>
    this.currentAssignments().filter(assignment => assignment.assigneeType === 'ORGANIZATION')
  );

  usersAvailableForAssignment = computed(() => {
    const assignedIds = new Set(this.assignedUsers().map(assignment => assignment.assigneeId));
    return this.availableUsers().filter(user => !assignedIds.has(user.id));
  });

  organizationsAvailableForAssignment = computed(() => {
    const assignedIds = new Set(this.assignedOrganizations().map(assignment => Number(assignment.assigneeId)));
    return this.availableOrganizations().filter(organization => !assignedIds.has(organization.id));
  });

  toggleUserSelection(userId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.selectedUserIds.update(current => {
      if (checked) {
        return current.includes(userId) ? current : [...current, userId];
      }

      return current.filter(id => id !== userId);
    });
  }

  toggleOrganizationSelection(organizationId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.selectedOrganizationIds.update(current => {
      if (checked) {
        return current.includes(organizationId) ? current : [...current, organizationId];
      }

      return current.filter(id => id !== organizationId);
    });
  }

  isUserSelected(userId: string): boolean {
    return this.selectedUserIds().includes(userId);
  }

  isOrganizationSelected(organizationId: number): boolean {
    return this.selectedOrganizationIds().includes(organizationId);
  }

  saveAssignments(): void {
    const alert = this.selectedAssignmentAlert();

    if (!alert || this.assignmentSaving()) {
      return;
    }

    if (!this.selectedUserIds().length && !this.selectedOrganizationIds().length) {
      this.assignmentError.set('Select at least one user or organization.');
      return;
    }

    this.assignmentSaving.set(true);
    this.assignmentError.set(null);

    this.alertService.addAssignments(alert.id, {
      userIds: this.selectedUserIds(),
      organizationIds: this.selectedOrganizationIds()
    }).subscribe({
      next: (assignments) => {
        this.currentAssignments.set(assignments);
        this.selectedUserIds.set([]);
        this.selectedOrganizationIds.set([]);
        this.assignmentSaving.set(false);
        this.loadAlerts();
      },
      error: (err) => {
        console.error('Failed to save assignments', err);
        this.assignmentError.set('Failed to save assignments.');
        this.assignmentSaving.set(false);
      }
    });
  }

  removeAssignment(assignment: AlertAssignment): void {
    const alert = this.selectedAssignmentAlert();

    if (!alert || this.assignmentSaving()) {
      return;
    }

    this.assignmentSaving.set(true);
    this.assignmentError.set(null);

    this.alertService.removeAssignment(alert.id, assignment.id).subscribe({
      next: () => {
        this.currentAssignments.update(current => current.filter(item => item.id !== assignment.id));
        this.assignmentSaving.set(false);
        this.loadAlerts();
      },
      error: (err) => {
        console.error('Failed to remove assignment', err);
        this.assignmentError.set('Failed to remove assignment.');
        this.assignmentSaving.set(false);
      }
    });
  }

  openAuditModal(alert: VigimareAlert): void {
    this.selectedAuditAlert.set(alert);
    this.auditModalOpen.set(true);
    this.auditLoading.set(true);
    this.auditError.set(null);
    this.auditEntries.set([]);

    this.alertService.getAudit(alert.id).subscribe({
      next: (entries) => {
        this.auditEntries.set(entries);
        this.auditLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load alert audit', err);
        this.auditError.set('Failed to load alert audit.');
        this.auditLoading.set(false);
      }
    });
  }

  closeAuditModal(): void {
    this.auditModalOpen.set(false);
    this.auditLoading.set(false);
    this.auditError.set(null);
    this.selectedAuditAlert.set(null);
    this.auditEntries.set([]);
  }

  getAuditActionLabel(entry: AlertAuditEntry): string {
    return entry.actionType === 'SHARED' ? 'shared this alert with' : 'removed';
  }

  getAuditTargetLabel(entry: AlertAuditEntry): string {
    return entry.targetType === 'USER' ? 'user' : 'organization';
  }

  getAssignmentIndicator(alert: VigimareAlert): string {
    return alert.assignedUsers?.length || alert.assignedOrganizations?.length
      ? 'Assigned'
      : 'Not assigned';
  }

  getStatusLabel(alert: VigimareAlert): string {
    return (alert.status ?? 'NEW').replaceAll('_', ' ');
  }

  getLastCommentLabel(alert: VigimareAlert): string {
    return alert.lastComment?.trim() || '-';
  }

  openCommentModal(alert: VigimareAlert): void {
    this.selectedCommentAlert.set(alert);
    this.commentText.set('');
    this.commentError.set(null);
    this.commentModalOpen.set(true);
  }

  closeCommentModal(): void {
    if (this.commentSaving()) {
      return;
    }

    this.commentModalOpen.set(false);
    this.commentError.set(null);
    this.selectedCommentAlert.set(null);
    this.commentText.set('');
  }

  saveComment(): void {
    const alert = this.selectedCommentAlert();
    const text = this.commentText().trim();

    if (!alert || this.commentSaving()) {
      return;
    }

    if (!text) {
      this.commentError.set('Comment text is required.');
      return;
    }

    this.commentSaving.set(true);
    this.commentError.set(null);

    this.alertService.addComment(alert.id, text).subscribe({
      next: () => {
        this.alerts.update(current =>
          current.map(item => item.id === alert.id ? { ...item, lastComment: text } : item)
        );
        this.commentSaving.set(false);
        this.closeCommentModal();
      },
      error: (err) => {
        console.error('Failed to save comment', err);
        this.commentError.set('Failed to save comment.');
        this.commentSaving.set(false);
      }
    });
  }

  filteredAlerts = computed(() => {
    const filter = this.globalFilter().trim().toLowerCase();

    if (!filter) {
      return this.alerts();
    }

    return this.alerts().filter((alert) => {
      const type = (this.getAlertType(alert) ?? '').toLowerCase();
      const legalName = (alert.legalName ?? '').toLowerCase();
      const summary = (this.getSummary(alert) ?? '').toLowerCase();

      return (
        type.includes(filter) ||
        legalName.includes(filter) ||
        summary.includes(filter)
      );
    });
  });

  sortedAlerts = computed(() => {
    const field = this.sortField();
    const direction = this.sortDirection();

    return [...this.filteredAlerts()].sort((a, b) => {
      let comparison = 0;

      if (field === 'id') {
        comparison = (a.id ?? 0) - (b.id ?? 0);
      }

      if (field === 'generatedIn') {
        comparison =
          new Date(a.generatedIn ?? '').getTime() -
          new Date(b.generatedIn ?? '').getTime();
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  });

  sortBy(field: 'generatedIn' | 'id'): void {
    if (this.sortField() === field) {
      this.sortDirection.set(
        this.sortDirection() === 'asc' ? 'desc' : 'asc'
      );
    } else {
      this.sortField.set(field);
      this.sortDirection.set(field === 'generatedIn' ? 'desc' : 'asc');
    }

    this.currentPage.set(1);
  }

  getSortIcon(field: 'generatedIn' | 'id'): string {
    if (this.sortField() !== field) {
      return '\u2195';
    }

    return this.sortDirection() === 'asc' ? '\u2191' : '\u2193';
  }

  onGlobalFilterChange(event: Event): void {
    this.globalFilter.set((event.target as HTMLInputElement).value);
    this.currentPage.set(1);
  }

  clearFilters(): void {
    this.globalFilter.set('');
    this.sortField.set('generatedIn');
    this.sortDirection.set('desc');
    this.currentPage.set(1);
  }

  totalItems = computed(() => this.sortedAlerts().length);

  totalPages = computed(() => {
    const total = Math.ceil(this.totalItems() / this.pageSize());
    return total > 0 ? total : 1;
  });

  paginatedAlerts = computed(() => {
    const page = this.currentPage();
    const size = this.pageSize();
    const start = (page - 1) * size;
    const end = start + size;
    return this.sortedAlerts().slice(start, end);
  });

  pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1)
  );

  ngOnInit(): void {
    this.loadAlerts();
    this.listenForAlertEvents();

    this.queryParamsSub = this.route.queryParams.subscribe(params => {
      const alertId = params['alertId'];
      this.highlightedAlertId.set(alertId ? +alertId : null);
      this.scrollToHighlightedAlert();
    });
  }

  listenForAlertEvents(): void {
    this.sseSubscription = this.alertService.listenForAlertEvents().subscribe({
      next: ({ type, alert }) => {
        this.alerts.update((currentAlerts) => {
          const index = currentAlerts.findIndex(existing => existing.id === alert.id);

          if (type === 'created') {
            if (index !== -1) {
              return currentAlerts;
            }

            return [alert, ...currentAlerts];
          }

          if (type === 'updated') {
            if (index === -1) {
              return [alert, ...currentAlerts];
            }

            const updatedAlerts = [...currentAlerts];
            updatedAlerts[index] = alert;

            return updatedAlerts;
          }

          return currentAlerts;
        });
      },
      error: (error) => {
        console.error('SSE connection error', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.sseSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
  }

  loadAlerts(): void {
    this.loading.set(true);
    this.error.set(null);

    this.alertService.getAll().subscribe({
      next: (data) => {
        this.alerts.set(data);
        this.loading.set(false);
        this.scrollToHighlightedAlert();
      },
      error: (err) => {
        console.error('Failed to load alerts', err);
        this.error.set('Failed to load alerts.');
        this.loading.set(false);
      }
    });
  }

  private scrollToHighlightedAlert(): void {
    const targetId = this.highlightedAlertId();
    if (!targetId) return;
    const index = this.sortedAlerts().findIndex(a => a.id === targetId);
    if (index === -1) return;
    this.currentPage.set(Math.floor(index / this.pageSize()) + 1);
  }

  deleteAlert(id: number): void {
    this.alertService.delete(id).subscribe({
      next: () => {
        const updatedAlerts = this.alerts().filter(alert => alert.id !== id);
        this.alerts.set(updatedAlerts);

        if (this.currentPage() > this.totalPages()) {
          this.currentPage.set(this.totalPages());
        }
      },
      error: () => {
        this.error.set('Failed to delete alert.');
      }
    });
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update(page => page + 1);
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(page => page - 1);
    }
  }

  onPageSizeChange(event: Event): void {
    const value = +(event.target as HTMLSelectElement).value;
    this.pageSize.set(value);
    this.currentPage.set(1);
  }

  getAlertType(alert: VigimareAlert): string {
    return alert.anomaly ? 'Anomaly' : 'Intention';
  }

  getSummary(alert: VigimareAlert): string {
    if (alert.anomaly) {
      return `${alert.anomaly.anomalyType} (${alert.anomaly.anomalyScore})`;
    }

    if (alert.intentions?.length) {
      return alert.intentions
        .map(i => `${i.classification} (${i.probability})`)
        .join(', ');
    }

    return '-';
  }

  grafanaIncidents =
    'https://rd.vigimare.simavi.ro/grafana/d/vigimare-incident-management/vigimare-incident-management' +
    '?orgId=1' +
    '&kiosk' +
    '&from=now-6h' +
    '&to=now' +
    'timezone=browser' +
    '&refresh=30s';
  protected readonly Math = Math;
}
